import { Anchor, Box, FlexBox, Stack, Text, TextSmall } from '@trmediaab/zebra';
import { Lock, PersonOutline, Spinner } from '@trmediaab/zebra-icons';
import type { Order } from '@trmediaab/zebra-types/trhub';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PartialDeep } from 'type-fest';

import AltContentDivider from '../../components/AltContentDivider';
import Cart from '../../components/Cart';
import ErrorMessage from '../../components/ErrorMessage';
import { Input } from '../../components/Form';
import Layout from '../../components/Layout';
import LoggedInUserCard from '../../components/LoggedInUserCard';
import NewUserCard from '../../components/NewUserCard';
import NextButton from '../../components/NextButton';
import ProgressBar from '../../components/ProgressBar';
import TextButton from '../../components/TextButton';
import VerificationCodeInput from '../../components/VerificationCodeInput';
import type { CheckoutState } from '../../state';
import { setOrder, useImmutableState } from '../../state';
import useOrderSubscription from '../../state/useOrderSubscription';
import useTicket from '../../state/useTicket';
import useUser from '../../state/useUser';
import type { ErrorWithMessage } from '../../types/ErrorWithMessage';
import type { OnAuthenticationHandler } from '../../types/OnAuthenticationHandler';
import { ensureErrorWithMessage } from '../../utils/ensureErrorWithMessage';
import { getProductSite, isShippingOrder } from '../../utils/orderUtils';
import request from '../../utils/request';
import { useAnalytics } from '../../utils/useAnalytics';
import useGeoLocation from '../../utils/useGeoLocation';
import useOrderPresentation from '../../utils/useOrderPresentation';
import Address from './Address';
import ChooseCountry from './ChooseCountry';
import PunchTicketModal from './PunchTicketModal';
import TicketCard from './TicketCard';
import type { NewOrExistingUserWithShippingAddress } from './types';
import validateAddress from './validateAddress';

function getAddressRequirements(order: NonNullable<CheckoutState['order']>) {
  if (order.isGiftPurchaseOrder) {
    return [false, false, false, false] as const;
  }

  const postal =
    order.lineItems?.some(lineItem => lineItem.product?.distributable) || false;

  const shipping = isShippingOrder(order) || false;
  const swedishPostalCode =
    order.lineItems?.some(
      lineItem => lineItem.product?.requireSwedishPostalCode,
    ) || false;

  return [postal, shipping, swedishPostalCode, postal || shipping] as const;
}

interface UserViewProps extends OnAuthenticationHandler {
  onNext: () => void;
}

// TODO - review loading states: submitting, user loading, ticket loading, user validating
const UserView = ({ onNext, onAuthentication }: UserViewProps) => {
  const {
    appMode,
    order: initialOrder,
    disallowUserChange,
    hasProductOptions,
    redeemCode,
  } = useImmutableState();

  if (initialOrder == null) {
    // Note: This should never happen, but we want to be sure
    throw new Error('Cannot render user view without order');
  }

  const [submitting, setSubmitting] = useState(false);
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const [values, setValues] = useState<{
    email?: string;
    password?: string;
    code?: string;
  }>({});
  const [addressValues, setAddressValues] = useState<
    PartialDeep<NewOrExistingUserWithShippingAddress> | undefined
  >();
  const [orderError, setOrderError] = useState<ErrorWithMessage | undefined>();
  const [ticketModalVisible, setTicketModalVisible] = useState(false);

  const [order, setLocalOrder] = useState(initialOrder);
  const {
    user,
    status,
    validateError,
    authError,
    lookupError,
    verifyError,
    isValidating: isValidatingUser,
    lookupByEmail,
    forgotPassword,
    authenticate,
    verify,
    logout,
    changeEmail,
  } = useUser(values, onAuthentication);
  const [ticket, ticketError, isLoadingTicket] = useTicket(order);
  const [
    requirePostalAddress,
    requireShippingAddress,
    requireSwedishPostalCode,
    requireAddress,
  ] = getAddressRequirements(order);
  const [geoCountry, _geoError, isLoadingGeo] = useGeoLocation(
    requireShippingAddress,
  );

  const lineItemPresentations = useOrderPresentation();
  let payButtonText = 'Gå till betalning';
  if (
    appMode === 'redeemGift' &&
    lineItemPresentations.length > 0 &&
    lineItemPresentations[0] != null
  ) {
    payButtonText = lineItemPresentations[0].buttonText;
  }
  const site = getProductSite(order);
  const analytics = useAnalytics();

  useOrderSubscription((_, order) => {
    // We use a local state instead of the snapshot from useCheckoutState
    // to be sure the submit-handler always has the most updated order.
    setLocalOrder(order);
  });

  const busy = isLoadingTicket || isValidatingUser || isLoadingGeo;

  if (ticketError != null) {
    // TODO handle ticket error
    console.info({ ticketError });
  }

  const requireVatCountry =
    !requireShippingAddress && !user?.address?.country && geoCountry !== 'SE';

  const isSubmittable = useCallback(() => {
    if (busy) {
      return false;
    }

    return (
      !requireAddress ||
      validateAddress(
        user,
        addressValues,
        requireShippingAddress,
        requireSwedishPostalCode,
      )
    );
  }, [
    addressValues,
    busy,
    requireAddress,
    requireShippingAddress,
    requireSwedishPostalCode,
    user,
  ]);

  const onLogoutClick = useCallback(() => {
    logout();
    setValues({});
    setAddressValues(undefined);
    setOrderError(undefined);
  }, [logout]);

  const onShowTicketModal = useCallback(() => {
    setTicketModalVisible(true);
  }, []);

  const onHideTicketModal = useCallback(() => {
    setTicketModalVisible(false);
  }, []);

  // Submit an order and navigate user to payment
  const onSubmit = useCallback(async () => {
    setSubmitting(true);

    try {
      let data: Order;

      if (appMode === 'redeemGift') {
        data = await request<Order>('gift/activate', {
          method: 'POST',
          body: {
            giftCode: redeemCode,
            email: values.email,
            addressValues,
          },
        });
      } else {
        data = await request<Order>('order', {
          method: 'POST',
          body: {
            lineItems: order.lineItems,
            campaign: order.campaign,
            email: values.email,
            addressValues,
          },
        });
      }
      setOrder(data);
      analytics.beginCheckout(data);
      onNext();
    } catch (error) {
      setOrderError(ensureErrorWithMessage(error));
      setSubmitting(false);
    }
  }, [
    appMode,
    analytics,
    onNext,
    redeemCode,
    values.email,
    addressValues,
    order.lineItems,
    order.campaign,
  ]);

  const onCountryChange = useCallback(
    (country: string) =>
      setAddressValues(prev => ({
        ...prev,
        address: { ...prev?.address, country },
      })),
    [],
  );

  useEffect(() => {
    // Nothing more to do here, submit and move to next step
    if (
      disallowUserChange &&
      user &&
      ticket == null &&
      isSubmittable() &&
      !submitting
    ) {
      setIsAutoSubmitting(true);
      onSubmit();
    }
  }, [disallowUserChange, isSubmittable, onSubmit, submitting, ticket, user]);

  const layoutAreas = useMemo(() => {
    if (requireShippingAddress) {
      return {
        oneCol: [
          'products',
          hasProductOptions && 'changeProduct',
          'shipping',
          'user',
        ].filter(Boolean),
        twoCol: [
          'products shipping',
          `${hasProductOptions ? 'changeProduct' : '.'} user`,
        ],
      };
    } else {
      return {
        oneCol: [
          'products',
          hasProductOptions && 'changeProduct',
          'user',
        ].filter(Boolean),
        twoCol: [
          'products user',
          `${hasProductOptions ? 'changeProduct' : '.'} user`,
        ],
      };
    }
  }, [hasProductOptions, requireShippingAddress]);

  return (
    <Layout
      areas={layoutAreas}
      sx={{
        // Set 1fr on row with change product-link so that it is placed directly
        // below card above
        gridTemplateRows: 'auto auto 1fr auto',
        // Allow for positioning ticket modal
        position: 'relative',
      }}
    >
      <ProgressBar progress={50} />

      <Cart
        order={order}
        editable
        showChangeProduct={hasProductOptions === true}
      />

      <Layout.area area="user">
        <Layout>
          {validateError != null && validateError.status !== 404 && (
            <ErrorMessage error={validateError} />
          )}

          {isAutoSubmitting || isValidatingUser ? (
            !orderError && (
              <Stack
                between="1,5"
                mx="auto"
                css={`
                  text-align: center;
                `}
              >
                <Spinner size="2em" />
                <TextSmall display="block">Kontrollerar kontot</TextSmall>
              </Stack>
            )
          ) : user ? (
            <>
              {!disallowUserChange && (
                <Stack between="3">
                  <LoggedInUserCard user={user} />
                  <TextButton
                    disabled={busy}
                    label="Byt konto"
                    onClick={onLogoutClick}
                  />
                </Stack>
              )}

              <Address
                required={
                  requireShippingAddress
                    ? 'shippingAddress'
                    : requirePostalAddress
                      ? 'address'
                      : undefined
                }
                user={user}
                disabled={busy}
                onChange={setAddressValues}
              />

              {requireVatCountry && (
                <ChooseCountry
                  defaultCountry={geoCountry || 'SE'}
                  order={order}
                  disabled={busy}
                  onChange={onCountryChange}
                />
              )}

              {ticket != null && <TicketCard ticket={ticket} />}
            </>
          ) : (
            <>
              {/* No user was found, a new account will be created */}
              {status === 'notfound' && (
                <>
                  <NewUserCard email={values.email as string} />
                  {requireVatCountry && (
                    <ChooseCountry
                      defaultCountry={geoCountry || 'SE'}
                      order={order}
                      disabled={busy}
                      onChange={onCountryChange}
                    />
                  )}
                  <Address
                    required={
                      requireShippingAddress
                        ? 'shippingAddress'
                        : requirePostalAddress
                          ? 'address'
                          : undefined
                    }
                    disabled={busy}
                    onChange={setAddressValues}
                  />
                </>
              )}

              <Stack between="3">
                <Input
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Ange din e-postadress"
                  value={values.email || ''}
                  disabled={status != null || busy}
                  Icon={PersonOutline}
                  onEnterKeyPress={lookupByEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setValues(prev => ({ ...prev, email: e.target.value }));
                  }}
                />
                <TextSmall
                  display="block"
                  textAlign="center"
                  color="text.faded"
                >
                  {site === 'jokersystemet_no' ? (
                    <>
                      Gjennom å trykke på <q>Gå videre</q> samtykker jeg til
                      Jokersystemet AS{' '}
                      <Anchor
                        href="https://www.jokersystemet.no/vilkar-og-betingelser"
                        target="_blank"
                        rel="noopener"
                        textDecoration="underline"
                      >
                        vilkår
                      </Anchor>{' '}
                      og bekrefter at jeg har lest Jokersystemet AS{' '}
                      <Anchor
                        href="https://www.jokersystemet.no/personvernerklring"
                        target="_blank"
                        rel="noopener"
                        textDecoration="underline"
                      >
                        personvernerklæring
                      </Anchor>
                      .
                    </>
                  ) : (
                    <>
                      Genom att trycka på <q>Gå vidare</q> samtycker jag till TR
                      Media ABs{' '}
                      <Anchor
                        href="https://www.trmedia.se/villkor"
                        target="_blank"
                        rel="noopener"
                        textDecoration="underline"
                      >
                        villkor
                      </Anchor>
                      , samt bekräftar att jag har läst TR Media ABs{' '}
                      <Anchor
                        href="https://www.trmedia.se/integritetspolicy"
                        target="_blank"
                        rel="noopener"
                        textDecoration="underline"
                      >
                        integritetspolicy
                      </Anchor>
                      .
                    </>
                  )}
                </TextSmall>

                {lookupError && <ErrorMessage error={lookupError} />}

                {status ? (
                  <TextButton
                    disabled={busy}
                    label="Byt e-postadress"
                    onClick={changeEmail}
                  />
                ) : (
                  <Box pt="1">
                    <NextButton
                      onClick={lookupByEmail}
                      disabled={values.email == null || busy}
                      label="Gå vidare"
                    />
                  </Box>
                )}
              </Stack>

              {/* User was found by email but is not authenticated, prompt login */}
              {status === 'exists' && (
                <Stack between="3">
                  <Text textAlign="center">
                    Du har redan ett konto hos oss, ange ditt lösenord för att
                    fortsätta.
                  </Text>

                  <Input
                    name="password"
                    type="password"
                    value={values.password || ''}
                    disabled={busy}
                    autoComplete="current-password"
                    placeholder="Ange ditt lösenord"
                    Icon={Lock}
                    onEnterKeyPress={authenticate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setValues(prev => ({
                        ...prev,
                        password: e.target.value,
                      }));
                    }}
                  />

                  {authError && (
                    <ErrorMessage error={authError} variant="input" />
                  )}

                  <TextButton
                    label="Glömt lösenord"
                    disabled={busy}
                    onClick={forgotPassword}
                  />

                  <Box pt="1">
                    <NextButton
                      onClick={authenticate}
                      disabled={busy}
                      label="Logga in"
                    />
                  </Box>
                </Stack>
              )}

              {/* An unverified user was found, prompt a verification code to proceed */}
              {status === 'unverified' && (
                <Stack between="3">
                  <Text textAlign="center">
                    En kod med fyra siffror har skickats till {values.email}
                  </Text>

                  <FlexBox justifyContent="center">
                    <VerificationCodeInput
                      length={4}
                      validChars="0-9"
                      inputProps={{ inputMode: 'numeric', disabled: busy }}
                      onChange={code => {
                        setValues(prev => ({ ...prev, code }));
                      }}
                    />
                  </FlexBox>

                  {verifyError && (
                    <ErrorMessage error={verifyError} variant="input" />
                  )}

                  <Box pt="1">
                    <NextButton
                      onClick={verify}
                      disabled={busy}
                      label="Verifiera"
                    />
                  </Box>
                </Stack>
              )}
            </>
          )}
        </Layout>
      </Layout.area>

      {(user || (!user && status === 'notfound')) && (
        <Layout.nextStepArea>
          <Stack between="2,5">
            {orderError && (
              <Box mb="2">
                <ErrorMessage error={orderError} />
              </Box>
            )}

            {ticket != null && !submitting && (
              <>
                <NextButton
                  label="Använd klippkort"
                  disabled={busy || !isSubmittable()}
                  enabled={isSubmittable()}
                  onClick={onShowTicketModal}
                />
                <AltContentDivider />
              </>
            )}

            {!isAutoSubmitting && (
              <NextButton
                label={payButtonText}
                disabled={busy || !isSubmittable()}
                variant={ticket != null ? 'secondary' : 'primary'}
                showSpinner={
                  validateError == null && (submitting || isValidatingUser)
                }
                onClick={onSubmit}
              />
            )}
          </Stack>
        </Layout.nextStepArea>
      )}
      {ticketModalVisible && (
        <PunchTicketModal order={order} onCancel={onHideTicketModal} />
      )}
    </Layout>
  );
};

export default UserView;
